import type { ChatMessage, NowPlaying } from "../api/types";

export const REALTIME_VERSION = 1 as const;
export const REALTIME_DOMAINS = [
  "segments",
  "listened",
  "preferences",
  "publications",
  "publishing-login",
  "settings",
  "access",
  "users",
  "sessions",
  "audit",
  "artwork",
  "events",
  "maintenance",
  "coverage",

  "playback",
  "chat",
  "nowplaying",
] as const;
function newClientId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export const REALTIME_CLIENT_ID = newClientId();
export const PLAYBACK_CHANGE_EVENT = "jtarchive:playback-change";

export type RealtimeDomain = (typeof REALTIME_DOMAINS)[number];

export interface RealtimeRange {
  start: string;
  end: string;
}

export type ClientFrame =
  | {
      type: "subscribe";
      version: typeof REALTIME_VERSION;
      domains: RealtimeDomain[];
      segment_ids: number[];
      timeline: RealtimeRange | null;
    }
  | { type: "presence"; client_id: string; playing: boolean };

export interface Change {
  domain: RealtimeDomain;
  ids?: (number | string)[];
  range?: RealtimeRange;
  reset?: true;
}

export type ServerFrame =
  | { type: "ready"; version: typeof REALTIME_VERSION; epoch: string; sequence: number }
  | { type: "changes"; epoch: string; sequence: number; changes: Change[] }
  | { type: "chat"; epoch: string; sequence: number; message: ChatMessage }
  | { type: "nowplaying"; epoch: string; sequence: number; patch: Partial<NowPlaying> };

const DOMAIN_SET = new Set<string>(REALTIME_DOMAINS);

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function envelope(value: unknown): value is Record<string, unknown> & {
  type: string;
  epoch: string;
  sequence: number;
} {
  return (
    record(value) &&
    typeof value.type === "string" &&
    typeof value.epoch === "string" &&
    value.epoch.length > 0 &&
    Number.isSafeInteger(value.sequence) &&
    (value.sequence as number) >= 0
  );
}

export function parseServerFrame(raw: string): ServerFrame | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!envelope(value)) return null;
  if (value.type === "ready") {
    return value.version === REALTIME_VERSION ? (value as unknown as ServerFrame) : null;
  }
  if (value.type === "changes") {
    if (
      !Array.isArray(value.changes) ||
      value.changes.some(
        (change) =>
          !record(change) || typeof change.domain !== "string" || !DOMAIN_SET.has(change.domain),
      )
    ) {
      return null;
    }
    return value as unknown as ServerFrame;
  }
  if (value.type === "chat")
    return record(value.message) ? (value as unknown as ServerFrame) : null;
  if (value.type === "nowplaying") {
    return record(value.patch) ? (value as unknown as ServerFrame) : null;
  }
  return null;
}
